import { Link } from '@tanstack/react-router';
import { Check, MessageSquareWarning } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { jevBudgetLabel, type ApprovalQueueItem } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { ModuleGate } from '../../../components/ModuleGate';
import { SkeletonLines } from '../../../components/Skeleton';
import { ToneIcon, slaTone, toneCardClass } from '../../../components/Tone';
import { useToneArea } from '../../../components/ToneArea';
import {
  EmptyState,
  ForbiddenState,
  ServerErrorState,
} from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatRelative } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { OfferApprovalsList } from '../../offers/components/OfferApprovalsList';
import { useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useApprovalQueue, useDecideApproval } from '../api/requisitions-api';
import { DecisionDialog } from '../components/DecisionDialog';
import { PriorityDot, SlaFigure } from '../components/RequisitionBadges';
import styles from './ApprovalQueuePage.module.css';

/**
 * REQ-04 — every approval waiting on the viewer: named for them, for a role they
 * hold where the requisition sits, or for someone they are covering (F2.11).
 *
 * E2's user story: "every pending hiring request … in one queue, with how long it's
 * been waiting". Oldest first, each with both SLA figures. Approve is one click and
 * the item leaves the list at once (CLAUDE.md §5a rule 1); reject and send back ask
 * for the reason first. S23 adds the offers waiting on the viewer (OFR-02) below, so an
 * approver has one place to look.
 */
export function ApprovalQueuePage(): ReactNode {
  const canView = useCanView('approval_action');
  const queue = useApprovalQueue();
  const decide = useDecideApproval();
  const showToast = useToasts((state) => state.show);
  const [deciding, setDeciding] = useState<ApprovalQueueItem | null>(null);
  const area = useToneArea();
  // Rule 13 (bug B9): with Time limits off, a clock neither shows nor colours the card.
  const isSlaOn = useModule('sla');

  if (!canView) return <ForbiddenState resource="approvals" contact="Your admin" />;
  if (queue.isError) return <ServerErrorState onRetry={() => void queue.refetch()} />;

  async function approve(item: ApprovalQueueItem): Promise<void> {
    try {
      await decide.mutateAsync({ stepId: item.stepId, version: item.stepVersion, decision: 'approve' });
      showToast({ tone: 'success', message: `${item.reference} approved`, detail: item.stepName });
    } catch (cause) {
      showToast({ tone: 'danger', message: `${item.reference} was not approved`, detail: errorMessage(cause) });
    }
  }

  const items = queue.data ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Approvals</h1>
        <p className={styles.lead}>
          {items.length === 0
            ? 'No requisitions are waiting on you.'
            : `${items.length} ${items.length === 1 ? 'requisition' : 'requisitions'} waiting on you, oldest first.`}
        </p>
      </header>

      {queue.isPending ? (
        <SkeletonLines count={4} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No requisitions to approve"
          body="When a requisition reaches a step you approve — or one for someone you are covering — it appears here, and you are told."
          action={null}
        />
      ) : (
        <ul className={styles.list}>
          {items.map((item) => {
            const tone = isSlaOn && item.sla ? slaTone(item.sla.state, area.tone) : area.tone;
            return (
              <li key={item.stepId} className={`${styles.item} ${toneCardClass()}`} data-tone={tone}>
                <div className={styles.itemMain}>
                  <div className={styles.itemTop}>
                    <ToneIcon tone={tone} />
                    <Link
                      to="/requisitions/$requisitionId"
                      params={{ requisitionId: item.requisitionId }}
                      className={styles.ref}
                    >
                      {item.reference}
                    </Link>
                    <PriorityDot priority={item.priority} />
                  </div>
                  <span className={styles.itemTitle}>{item.title}</span>
                  <span className={styles.meta}>
                    {item.slotsRequested} {item.slotsRequested === 1 ? 'hire' : 'hires'} · {item.departmentName},{' '}
                    {item.ventureName}
                    {item.requestedByName ? ` · raised by ${item.requestedByName}` : ''}
                    {item.jevBudgetMicroUsd === null ? '' : ` · Jev budget ${jevBudgetLabel(item.jevBudgetMicroUsd)}`}
                  </span>
                  <span className={styles.meta}>
                    {item.stepName} · waiting {formatRelative(item.waitingSince)}
                    {item.onBehalfOfName ? ` · covering for ${item.onBehalfOfName}` : ''}
                  </span>
                  <SlaFigure sla={item.sla} showLimit />
                </div>
                <div className={styles.itemActions}>
                  <Button variant="solid" icon={Check} onClick={() => void approve(item)}>
                    Approve
                  </Button>
                  <Button variant="outline" icon={MessageSquareWarning} onClick={() => setDeciding(item)}>
                    {item.jevBudgetMicroUsd === null ? 'Reject or send back' : 'Change amount, reject or send back'}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ModuleGate module="offers">
        <Card title="Offers" meta="Offers waiting on your approval · figures shown because the step is yours">
          <OfferApprovalsList />
        </Card>
      </ModuleGate>

      {deciding && (
        <DecisionDialog
          stepId={deciding.stepId}
          stepVersion={deciding.stepVersion}
          reference={deciding.reference}
          title={deciding.stepName}
          onBehalfOfName={deciding.onBehalfOfName}
          jevBudgetMicroUsd={deciding.jevBudgetMicroUsd}
          onClose={() => setDeciding(null)}
        />
      )}
    </div>
  );
}
